import React, { useRef, useEffect } from "react"
import { Box, Divider } from "@mui/material"
import { MultilangJob, MultiLangJobStatus, getMultilangJobs, MultilangJobFilters } from "src/services/api/document"
import { useTranslation } from "react-i18next"
import { useInterval } from "src/utils/useInterval"
import { useResponsive } from "src/utils/useResponsive"
import HorizontalCardCarousel from "./HorizontalCarousel"

interface MyFilesMobileProps {
  setMultilangJob: (multilangJob?: MultilangJob) => void
  filters: MultilangJobFilters
  onOpenEmailModal: () => void
  updatedJob?: MultilangJob
}

const MyFilesMobile: React.FC<MyFilesMobileProps> = ({ setMultilangJob, filters, onOpenEmailModal, updatedJob }) => {
  const { t } = useTranslation("common")
  const { isMobile } = useResponsive()

  // Store refresh functions from all carousels
  const refreshFunctionRefs = useRef<{
    inProgress?: () => Promise<void>
    finished?: () => Promise<void>
  }>({})

  const fetchInProgressMultilangJobs = (offset: number, limit: number) =>
    getMultilangJobs({
      sortBy: "uploadDate",
      sortDirection: "desc",
      offset,
      limit,
      ...filters,
      statuses: [MultiLangJobStatus.IN_PROGRESS].join(",")
    })

  const fetchFinishedMultilangJobs = (offset: number, limit: number) =>
    getMultilangJobs({
      sortBy: "deliveryDate",
      sortDirection: "desc",
      offset,
      limit,
      ...filters,
      statuses: [MultiLangJobStatus.FINISHED].join(",")
    })

  const refresh = async () => {
    // Trigger refresh on all carousels
    const promises = Object.values(refreshFunctionRefs.current).filter(Boolean)
    await Promise.all(promises.map((fn) => fn?.()))
  }

  useEffect(() => {
    refresh()
  }, [filters])

  // Add 10-second interval for centralized refresh
  useInterval(refresh, 10000)

  const showFinished = filters.statuses?.includes(MultiLangJobStatus.FINISHED) || !filters.statuses
  const showInProgress = filters.statuses?.includes(MultiLangJobStatus.IN_PROGRESS) || !filters.statuses

  return (
    <Box
      sx={
        isMobile
          ? { width: "100vw", overflowX: "hidden", display: "flex", flexDirection: "column", flex: 1, height: "100%" }
          : {}
      }
    >
      {showInProgress && (
        <>
          <HorizontalCardCarousel
            fetchItems={fetchInProgressMultilangJobs}
            cardWidth={300}
            gap={16}
            showDeadline
            onInfoClick={setMultilangJob}
            browserTitle={t("files.IN_PROGRESS")}
            onRefreshReady={(refreshFn) => {
              refreshFunctionRefs.current.inProgress = refreshFn
            }}
            onOpenEmailModal={onOpenEmailModal}
            updatedJob={updatedJob}
          />
          {showFinished && <Divider />}
        </>
      )}
      {showFinished && (
        <HorizontalCardCarousel
          fetchItems={fetchFinishedMultilangJobs}
          cardWidth={300}
          gap={16}
          showDeadline
          showDownloadButton
          onInfoClick={setMultilangJob}
          browserTitle={t("files.completed")}
          onRefreshReady={(refreshFn) => {
            refreshFunctionRefs.current.finished = refreshFn
          }}
          onOpenEmailModal={onOpenEmailModal}
          updatedJob={updatedJob}
        />
      )}
    </Box>
  )
}

export default MyFilesMobile
